/**
 * utils.js — Shared helpers and formatters
 *
 * Mirrors the v1 utility functions (fmtD, fmtC, fmtT, uid)
 * plus new React-specific helpers.
 */

// ── Date Helpers ─────────────────────────────────────────────────────────────

/**
 * Convert a Date (or date string) to 'YYYY-MM-DD' using the browser's local timezone.
 * Avoids the common bug where .toISOString() shifts dates to UTC.
 */
export function toLocalDateStr(d) {
  if (!d) return '';
  let dt;
  if (typeof d === 'string') {
    const dateOnly = d.includes('T') ? d.split('T')[0] : d;
    dt = new Date(dateOnly + 'T00:00:00');
  } else {
    dt = d;
  }
  if (isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

// ── Date Formatting ──────────────────────────────────────────────────────────

/**
 * The date an invoice payment was recorded, as YYYY-MM-DD ('' if unknown).
 *
 * Two writers put different shapes into invoices.payments: the invoice page
 * writes { date, ... } while the Job Pipeline writes { at: <ISO>, ... }. Every
 * reader MUST go through this helper — keying off p.date directly makes
 * `at`-shaped entries evaluate to '' and silently drop out of every total while
 * the invoice still reads "paid". That hid $6,322.50 of collected cash across
 * nine invoices (audit V-02, 2026-07-27).
 */
export function paymentDate(p) {
  if (!p) return '';
  const raw = p.date || p.at;
  if (!raw) return '';
  const s = String(raw);
  return s.length >= 10 ? s.slice(0, 10) : '';
}

/**
 * Format a date string as "Mar 15, 2026"
 * Returns '—' for empty/invalid dates.
 */
export function fmtD(dateStr) {
  if (!dateStr) return '—';
  const s = String(dateStr);
  const dateOnly = s.includes('T') ? s.split('T')[0] : s;
  const d = new Date(dateOnly + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Format a time string as "9:00 AM"
 * Accepts "HH:MM" or "HH:MM:SS" format.
 */
export function fmtT(timeStr) {
  if (!timeStr) return '—';
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h)) return '—';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

// ── Currency Formatting ──────────────────────────────────────────────────────

/**
 * Format a number as "$1,250.00"
 * Handles null/undefined/NaN gracefully.
 */
export function fmtC(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(n);
}

/**
 * Compact currency — "$1.2k" style for dashboards.
 */
export function fmtCCompact(amount) {
  const n = parseFloat(amount);
  if (isNaN(n)) return '$0';
  if (Math.abs(n) >= 1000) {
    return `$${(n / 1000).toFixed(1)}k`;
  }
  return fmtC(n);
}

// ── ID Generation ────────────────────────────────────────────────────────────

/**
 * Generate a unique ID — same format as v1.
 * Timestamp-based with random suffix for uniqueness.
 */
export function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

/**
 * Next invoice number — "INV-YYMMDD-NNN", daily sequence.
 * Scans existing invoices for today's prefix and increments the max.
 * Mirrors the logic in QuickInvoiceModal / InvoiceForm.
 */
export function nextInvoiceNumber(settings, allInvoices = []) {
  const prefix = settings?.invoicePrefix || 'INV';
  const startNum = parseInt(settings?.invoiceStart || '1', 10) || 1;
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const dateStr = `${yy}${mm}${dd}`;
  const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^${escaped}-${dateStr}-(\\d+)$`);
  let maxNum = startNum - 1;
  allInvoices.forEach((inv) => {
    const match = (inv.number || '').match(pattern);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxNum) maxNum = num;
    }
  });
  return `${prefix}-${dateStr}-${String(maxNum + 1).padStart(3, '0')}`;
}

// ── Field Mapping (camelCase ↔ snake_case) ───────────────────────────────────

/**
 * Convert a snake_case DB row to camelCase JS object.
 * Handles nested jsonb fields without modifying them.
 */
export function snakeToCamel(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
    result[camelKey] = value;
  }
  return result;
}

/**
 * Convert a camelCase JS object to snake_case for DB writes.
 */
export function camelToSnake(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase());
    result[snakeKey] = value;
  }
  return result;
}

// ── String Helpers ───────────────────────────────────────────────────────────

/**
 * Truncate text with ellipsis.
 */
export function truncate(str, len = 40) {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
}

/**
 * Capitalize first letter of a string.
 */
export function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format a DB status value for display (handles underscores).
 * e.g., 'post_production' → 'Post Production', 'partial_delivery' → 'Partial Delivery'
 */
const STATUS_LABELS = {
  intake: 'Intake',
  quoted: 'Quoted',
  confirmed: 'Confirmed',
  shooting: 'Shooting',
  post_production: 'Post Production',
  partial_delivery: 'Partial Delivery',
  delivered: 'Delivered',
  invoiced: 'Invoiced',
  paid: 'Paid',
  archived: 'Archived',
  void: 'Void',
};
export function formatStatus(status) {
  return STATUS_LABELS[status] || capitalize(status);
}

// ── Tailwind className helper ────────────────────────────────────────────────

/**
 * Merge class names, filtering out falsy values.
 * Usage: cn('base-class', isActive && 'active-class', className)
 */
export function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

// ── Status Utilities ─────────────────────────────────────────────────────────

/**
 * Job status → display color mapping (matches v1 STATUS_COLORS).
 */
export const STATUS_COLORS = {
  intake: '#A47B1A',
  quoted: '#1a7fa3',
  confirmed: '#7049a8',
  booked: '#7049a8',           // legacy alias
  shooting: '#2E7D9B',
  post_production: '#5B5FC7',
  inpost: '#5B5FC7',           // legacy alias
  partial_delivery: '#C67A1E',
  delivered: '#1e8b4a',
  complete: '#A47B1A',         // legacy alias
  invoiced: '#c43d28',
  paid: '#1e8b4a',
  void: '#dc2626',
  archived: '#78716C',
};

/**
 * Job status flow — the canonical order.
 */
export const STATUS_FLOW = [
  'intake',
  'quoted',
  'confirmed',
  'shooting',
  'post_production',
  'partial_delivery',
  'delivered',
  'invoiced',
  'paid',
  'archived',
];

/**
 * Invoice status colors.
 */
export const INVOICE_STATUS_COLORS = {
  unpaid: '#A47B1A',
  sent: '#1a7fa3',
  partial: '#B8860B',
  paid: '#1e8b4a',
  overdue: '#D04A32',
  void: '#dc2626',
};

// ── Debounce ─────────────────────────────────────────────────────────────────

/**
 * Simple debounce for search inputs, etc.
 */
export function debounce(fn, ms = 300) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

/**
 * Generate actual charge occurrences for a static/recurring expense within a year.
 * Returns array of { monthKey: 'YYYY-MM', amount: number }.
 * Respects chargeDate, expiresDate, and cancelledAt — no charges after end date.
 */
export function getStaticCharges(se, year) {
  const amount = parseFloat(se.amount) || 0;
  if (!amount) return [];

  const freq = se.frequency || 'monthly';

  // chargeDate can be a full date "2026-04-15" or a day-of-month "15"
  let start = null;
  let anchorDay = 1;
  if (se.chargeDate) {
    if (se.chargeDate.length <= 2) {
      anchorDay = parseInt(se.chargeDate) || 1;
    } else {
      start = new Date(se.chargeDate + 'T12:00');
      anchorDay = start.getDate();
    }
  }

  // Determine the cutoff date (cancelled or expired, whichever is earlier)
  let cutoff = null;
  if (se.cancelledAt) {
    const c = new Date(se.cancelledAt);
    if (!cutoff || c < cutoff) cutoff = c;
  }
  if (se.expiresDate) {
    const e = new Date(se.expiresDate + 'T12:00');
    if (!cutoff || e < cutoff) cutoff = e;
  }

  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31, 23, 59, 59);
  const charges = [];

  const pushCharge = (d) => {
    if (d < yearStart || d > yearEnd) return;
    if (start && d < start) return;
    if (cutoff && d > cutoff) return;
    charges.push({
      monthKey: `${year}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      amount,
    });
  };

  if (freq === 'one-time') {
    if (start) pushCharge(start);
    return charges;
  }

  if (freq === 'weekly') {
    // Generate all weekly charges across the year
    let d = start ? new Date(start) : new Date(year, 0, 1, 12);
    if (d < yearStart) {
      const weeks = Math.ceil((yearStart - d) / (7 * 86400000));
      d = new Date(d.getTime() + weeks * 7 * 86400000);
    }
    while (d <= yearEnd) {
      if (cutoff && d > cutoff) break;
      if (!start || d >= start) {
        charges.push({
          monthKey: `${year}-${String(d.getMonth() + 1).padStart(2, '0')}`,
          amount,
        });
      }
      d = new Date(d.getTime() + 7 * 86400000);
    }
    return charges;
  }

  // Monthly, quarterly, annual — iterate by month, clamp day to last day of month
  const step = freq === 'quarterly' ? 3 : (freq === 'annual' || freq === 'yearly') ? 12 : 1;

  // Find first occurrence month in the year
  let firstMonth = 0;
  if (start && start.getFullYear() === year) {
    firstMonth = start.getMonth();
  } else if (start && start.getFullYear() < year) {
    // Align to the recurrence cycle
    const monthsSinceStart = (year - start.getFullYear()) * 12 - start.getMonth();
    const rem = monthsSinceStart % step;
    firstMonth = rem === 0 ? 0 : step - rem;
  } else if (start && start.getFullYear() > year) {
    return charges; // hasn't started yet
  }

  for (let m = firstMonth; m < 12; m += step) {
    const lastDay = new Date(year, m + 1, 0).getDate();
    const day = Math.min(anchorDay, lastDay);
    pushCharge(new Date(year, m, day, 12));
  }

  return charges;
}
